import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  Chart as ChartJS, 
  CategoryScale, 
  LinearScale, 
  PointElement, 
  LineElement, 
  Title, 
  Tooltip, 
  Legend, 
  Filler,
  ArcElement
} from 'chart.js';
import { Line, Pie } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  ArcElement
);

export default function Dashboard() {
    const navigate = useNavigate();
    const [user, setUser] = useState(null);
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [attestations, setAttestations] = useState([]);
    const [latest, setLatest] = useState({ power_kwh: 0, timestamp: '' });
    const [globalStats, setGlobalStats] = useState({ total_energy_mwh: 0 });
    const [telemetry, setTelemetry] = useState([]);
    const [loading, setLoading] = useState(true);
    const [tab, setTab] = useState('energy');
    const [searchQuery, setSearchQuery] = useState('');
    const [qrModal, setQrModal] = useState(null);
    const [rawRecords, setRawRecords] = useState([]);
    const [plants, setPlants] = useState([]);
    const [selectedPlantId, setSelectedPlantId] = useState('all');

    const getPlantName = (id) => {
        if (!id) return 'N/A';
        // Handle seed projects first
        if (id === 'hydro-pokhara-001') return 'Pokhara Green Hydro';
        
        const plant = plants.find(p => p.id.toString() === id.toString());
        return plant ? plant.plant_name : `Plant ${id}`;
    };

    const fetchData = async () => {
        try {
            const token = localStorage.getItem('token');
            const fetchers = [
                fetch('/api/attestations'),
                fetch('/api/sensor/latest'),
                fetch('/api/telemetry/24h'),
                fetch('/api/stats/global')
            ];
            if (token) {
                fetchers.push(fetch('/api/plants', {
                    headers: { 'Authorization': `Bearer ${token}` }
                }));
                const telemetryUrl = selectedPlantId === 'all' 
                    ? '/api/sensor/history?limit=30' 
                    : `/api/plants/${selectedPlantId}/generation/latest?limit=20`;
                fetchers.push(fetch(telemetryUrl, {
                    headers: { 'Authorization': `Bearer ${token}` }
                }));
            }

            const responses = await Promise.all(fetchers);
            const dataA = await responses[0].json();
            const dataL = await responses[1].json();
            const dataT = await responses[2].json();
            const dataG = await responses[3].json();
            
            let dataP = [];
            let dataR = [];
            if (token && responses[4] && responses[5]) {
                dataP = await responses[4].json();
                dataR = await responses[5].json();
            }

            setAttestations(Array.isArray(dataA) ? dataA.sort((a,b) => new Date(b.timestamp) - new Date(a.timestamp)) : []);
            setLatest(dataL);
            setTelemetry(dataT);
            setRawRecords(Array.isArray(dataR) ? dataR : []);
            setPlants(Array.isArray(dataP) ? dataP : []);
            setGlobalStats(dataG);
            setLoading(false);
        } catch (err) {
            console.error("Dashboard fetch error:", err);
        }
    };

    useEffect(() => {
        const uJson = localStorage.getItem('user');
        if (uJson) {
            setUser(JSON.parse(uJson));
            setIsLoggedIn(true);
        }
        fetchData();
        const interval = setInterval(fetchData, 5000);
        return () => clearInterval(interval);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedPlantId]);

    useEffect(() => {
        if (qrModal && window.QRCode) {
            setTimeout(() => {
                window.QRCode.toCanvas(document.getElementById('qr-canvas'), qrModal, { width: 220, margin: 2, color: { dark: '#0f172a', light: '#ffffff' } }, (err) => err && console.error(err));
            }, 50);
        }
    }, [qrModal]);

    const activeCount = attestations.filter(a => a.status === 'active').length;
    const pendingCount = attestations.filter(a => a.status === 'pending_expert').length;
    const retiredCount = attestations.filter(a => a.status === 'retired').length;

    const lineData = {
        labels: telemetry.map(t => t.hour),
        datasets: [{
            label: 'Hourly MWh Output',
            data: telemetry.map(t => t.mwh),
            borderColor: '#10b981',
            backgroundColor: (context) => {
                const ctx = context.chart.ctx;
                const gradient = ctx.createLinearGradient(0, 0, 0, 400);
                gradient.addColorStop(0, 'rgba(16, 185, 129, 0.4)');
                gradient.addColorStop(1, 'rgba(16, 185, 129, 0)');
                return gradient;
            },
            fill: true,
            tension: 0.4,
            pointRadius: 4,
            pointBackgroundColor: '#10b981',
            pointBorderColor: '#fff',
        }]
    };

    const pieData = {
        labels: ['Active', 'Pending', 'Retired'],
        datasets: [{
            data: [activeCount, pendingCount, retiredCount],
            backgroundColor: [
                'rgba(16, 185, 129, 0.8)',
                'rgba(245, 158, 11, 0.8)',
                'rgba(59, 130, 246, 0.8)'
            ],
            borderColor: 'rgba(255, 255, 255, 0.1)',
            borderWidth: 2,
        }]
    };

    const uiOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { 
            legend: { display: false },
            tooltip: {
                backgroundColor: 'rgba(15, 23, 42, 0.9)',
                titleFont: { size: 10, weight: 'bold' },
                bodyFont: { size: 12 },
                padding: 12,
                cornerRadius: 12,
                displayColors: false
            }
        },
        scales: {
            y: { grid: { color: 'rgba(255,255,255,0.05)' }, ticks: { color: '#64748b', font: { size: 10 } } },
            x: { grid: { display: false }, ticks: { color: '#64748b', font: { size: 10 } } }
        }
    };

    const energies = attestations.filter(a => !a.isPhysical).slice(0, 15);
    const physicals = attestations.filter(a => a.isPhysical).filter(a => {
        if (!searchQuery) return true;
        const q = searchQuery.toLowerCase();
        const metaStr = typeof a.metadata === 'object' ? JSON.stringify(a.metadata) : (a.metadata || '');
        return (a.docType || '').toLowerCase().includes(q) || 
               (a.issuer || '').toLowerCase().includes(q) || 
               metaStr.toLowerCase().includes(q);
    });

    if (loading) return <div className="h-screen flex items-center justify-center bg-[#080a0f] text-emerald-500 font-black tracking-widest animate-pulse">SYNCHRONIZING GREEN LEDGER...</div>;

    return (
        <div className="p-4 sm:p-8 max-w-7xl mx-auto animate-fade-in">
            {/* Header */}
            <header className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 mb-10">
                <Link to="/" className="group">
                    <h1 className="text-3xl font-black italic tracking-tight bg-gradient-to-r from-emerald-400 to-cyan-400 bg-clip-text text-transparent group-hover:scale-105 transition-transform">Silicon Himalayas</h1>
                    <p className="text-[10px] text-slate-500 uppercase tracking-[0.3em] font-black group-hover:text-emerald-500 transition-colors">Green Attestation Ledger (Mainnet)</p>
                </Link>
                <div className="flex flex-wrap items-center gap-4 w-full md:w-auto">
                    {isLoggedIn ? (
                        <>
                            <div className="text-right hidden sm:block">
                                <p className="text-sm font-bold text-white leading-none">{user?.full_name}</p>
                                <p className="text-[10px] text-emerald-400 uppercase tracking-widest font-bold mt-1">{(user?.role || 'Guest')} Account</p>
                            </div>
                            {user?.role === 'expert' && (
                                <Link to="/dashboard/expert" className="px-4 py-2 bg-emerald-500/20 hover:bg-emerald-500/40 text-emerald-400 border border-emerald-500/30 rounded-xl transition text-xs font-black uppercase tracking-wider flex items-center gap-2">
                                    <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></span>
                                    Audit Terminal
                                </Link>
                            )}
                            {user?.role === 'investor' && (
                                <Link to="/dashboard/investor" className="px-4 py-2 bg-emerald-500/20 hover:bg-emerald-500/40 text-emerald-400 border border-emerald-500/30 rounded-xl transition text-xs font-black uppercase tracking-wider flex items-center gap-2">
                                    <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></span>
                                    Portfolio Terminal
                                </Link>
                            )}
                            {user?.role === 'observer' && (
                                <Link to="/dashboard/observer" className="px-4 py-2 bg-indigo-500/20 hover:bg-indigo-500/40 text-indigo-300 border border-indigo-500/30 rounded-xl transition text-xs font-black uppercase tracking-wider flex items-center gap-2">
                                    <span className="w-2 h-2 bg-indigo-500 rounded-full animate-pulse"></span>
                                    Observer Terminal
                                </Link>
                            )}

                            {user?.role === 'admin' && (
                                <Link to="/admin" className="px-4 py-2 bg-cyan-500/20 hover:bg-cyan-500/40 text-cyan-400 border border-cyan-500/30 rounded-xl transition text-xs font-black uppercase tracking-wider flex items-center gap-2">
                                    <span className="w-2 h-2 bg-cyan-500 rounded-full animate-pulse"></span>
                                    Control Plane
                                </Link>
                            )}
                            <div className="flex items-center space-x-2">
                                <span className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></span>
                                <span className="text-xs font-medium text-slate-300">Synchronized</span>
                            </div>
                            <button 
                                onClick={() => { localStorage.clear(); setIsLoggedIn(false); setUser(null); navigate('/login'); }}
                                className="px-4 py-2 bg-rose-500/10 hover:bg-rose-500/20 text-rose-400 border border-rose-500/30 rounded-xl transition text-xs font-bold uppercase"
                            >
                                Sign Out
                            </button>
                        </>
                    ) : (
                        <div className="flex items-center space-x-3">
                            <Link to="/login" className="px-4 py-2 text-slate-300 hover:text-white transition text-xs font-bold uppercase">Sign In</Link>
                            <Link to="/register" className="px-4 py-2 bg-emerald-500/20 hover:bg-emerald-500/40 text-emerald-400 border border-emerald-500/30 rounded-xl transition text-xs font-bold uppercase">Register</Link>
                        </div>
                    )}
                </div>
            </header>

            {/* Stats Row */}
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 mb-8">
                <div className="glass p-8 rounded-[2.5rem] flex flex-col justify-between border-emerald-500/10 shadow-[0_20px_50px_rgba(16,185,129,0.05)]">
                    <div>
                        <h3 className="text-slate-500 text-[10px] font-black uppercase tracking-widest mb-4">Total Generation</h3>
                        <div className="text-5xl font-black text-white tracking-tighter italic">{globalStats.total_energy_mwh} <span className="text-xl opacity-30 not-italic">MWh</span></div>
                    </div>
                    <div className="mt-6 pt-6 border-t border-white/5">
                        <h3 className="text-slate-500 text-[10px] font-black uppercase tracking-widest mb-2">Live Energy Feed</h3>
                        <div className="text-3xl font-black text-emerald-500 tracking-tighter italic">{latest.power_kwh || latest.increment_kwh || 0} <span className="text-xs opacity-30 not-italic">kWh</span></div>
                    </div>
                    <p className="text-[10px] font-mono text-slate-600 mt-4">NETWORK_SYNC: {new Date(latest.timestamp).toLocaleTimeString()}</p>
                </div>
                <div className="lg:col-span-2 glass p-8 rounded-[2.5rem] h-80 relative border-white/5 shadow-2xl overflow-hidden">
                    <div className="flex justify-between items-start mb-6">
                        <h3 className="text-slate-500 text-[10px] font-black uppercase tracking-widest">24h Energy Yield (Hourly MWh)</h3>
                        <div className="flex items-center gap-2">
                            <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></span>
                            <span className="text-[9px] font-black text-emerald-500 uppercase">Live Indexing</span>
                        </div>
                    </div>
                    <div className="h-56">
                        <Line data={lineData} options={uiOptions} />
                    </div>
                </div>
                <div className="glass p-8 rounded-[2.5rem] border-white/5 shadow-2xl h-80 overflow-hidden">
                    <h3 className="text-slate-500 text-[10px] font-black uppercase tracking-widest mb-6">Ledger Distribution</h3>
                    <div className="h-48">
                        <Pie data={pieData} options={{...uiOptions, plugins: { ...uiOptions.plugins, legend: { display: true, position: 'bottom', labels: { color: '#64748b', font: { size: 9, weight: 'bold' }, padding: 15, boxWidth: 10, usePointStyle: true } } } }} />
                    </div>
                </div>
            </div>

            {/* Tabs */}
            <div className="flex flex-wrap gap-2 mb-6">
                <button onClick={() => setTab('energy')} className={`px-6 py-2 rounded-xl transition text-sm font-semibold shadow border ${tab==='energy' ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/40' : 'glass text-slate-400 border-transparent hover:bg-white/5'}`}>Energy Outputs</button>
                <button onClick={() => setTab('live')} className={`px-6 py-2 rounded-xl transition text-sm font-semibold shadow border ${tab==='live' ? 'bg-cyan-500/20 text-cyan-400 border-cyan-500/40' : 'glass text-slate-400 border-transparent hover:bg-white/5'}`}>Live Telemetry</button>
                <button onClick={() => setTab('physical')} className={`px-6 py-2 rounded-xl transition text-sm font-semibold shadow border ${tab==='physical' ? 'bg-blue-500/20 text-blue-400 border-blue-500/40' : 'glass text-slate-400 border-transparent hover:bg-white/5'}`}>Physical Certificates</button>
            </div>

            {/* Energy Tab Content */}
            {tab === 'energy' && (
            <div className="glass rounded-3xl overflow-hidden">
                <div className="p-6 border-b border-white/10">
                    <h2 className="text-xl font-semibold">Recent Attestations</h2>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-left">
                        <thead className="bg-white/5 text-slate-400 text-sm">
                            <tr>
                                <th className="p-4">Attestation ID</th>
                                <th className="p-4">Project</th>
                                <th className="p-4">MWh</th>
                                <th className="p-4">Timestamp</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-white/5">
                            {energies.map(a => (
                                <tr key={a.id} className="hover:bg-white/5 transition">
                                    <td className="p-4 font-mono text-xs opacity-75">{a.id.substring(0, 18)}...</td>
                                    <td className="p-4 font-bold text-emerald-400">
                                        <Link to={`/plant/${a.projectId || a.plantId}`} className="hover:underline hover:text-emerald-350 transition-colors">
                                            {getPlantName(a.projectId || a.plantId)}
                                        </Link>
                                    </td>
                                    <td className="p-4 accent-green font-semibold">{a.mwh}</td>
                                    <td className="p-4 text-slate-400 text-sm">{new Date(a.timestamp).toLocaleString()}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
            )}

            {/* Live Telemetry Tab Content */}
            {tab === 'live' && (
            <div className="glass rounded-3xl overflow-hidden animate-fade-in">
                <div className="p-6 border-b border-white/10 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-slate-900/50">
                    <div className="flex flex-wrap items-center gap-4 w-full sm:w-auto">
                        <h2 className="text-xl font-semibold">Real-time Sensor Feed</h2>
                        <select 
                            value={selectedPlantId} 
                            onChange={(e) => setSelectedPlantId(e.target.value)}
                            className="bg-slate-800 border border-white/10 text-emerald-400 text-xs font-bold rounded-lg px-3 py-1.5 focus:outline-none focus:border-emerald-500/50 transition-all w-full sm:w-auto"
                        >
                            <option value="all">🌐 All Plants / Devices</option>
                            {plants.map(p => (
                                <option key={p.id} value={p.id}>{p.plant_name} (ID: {p.id})</option>
                            ))}
                        </select>
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="w-2 h-2 bg-cyan-500 rounded-full animate-pulse"></span>
                        <span className="text-[10px] text-cyan-500 font-bold uppercase tracking-widest">Streaming</span>
                    </div>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-left">
                        <thead className="bg-white/5 text-slate-400 text-sm">
                            <tr>
                                <th className="p-4">Timestamp</th>
                                <th className="p-4">Plant</th>
                                <th className="p-4">Device ID</th>
                                <th className="p-4">Increment (kWh)</th>
                                <th className="p-4">Status</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-white/5">
                            {rawRecords.map((r, i) => (
                                <tr key={i} className="hover:bg-white/5 transition">
                                    <td className="p-4 text-slate-400 text-sm font-mono">{new Date(r.timestamp).toLocaleTimeString()}</td>
                                    <td className="p-4 font-bold text-xs text-slate-300">{getPlantName(r.plant_id)}</td>
                                    <td className="p-4 font-mono text-xs opacity-75">{r.device_id}</td>
                                    <td className="p-4 text-cyan-400 font-bold">+{r.increment_kwh}</td>
                                    <td className="p-4">
                                        <span className="text-[10px] bg-cyan-500/10 text-cyan-400 px-2 py-1 rounded border border-cyan-500/20 uppercase font-bold">Recorded</span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
            )}

            {/* Physical Certificates Tab Content */}
            {tab === 'physical' && (
            <div className="glass rounded-3xl overflow-hidden animate-fade-in-up transition-all">
                <div className="p-6 border-b border-white/10 flex justify-between items-center bg-slate-800/20">
                    <h2 className="text-xl font-semibold">Physical Documentation</h2>
                    <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 opacity-50"><svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path></svg></span>
                        <input type="text" placeholder="Search Filter..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-9 bg-slate-900 border border-slate-700/50 rounded-xl px-4 py-2 text-sm focus:outline-none focus:border-blue-500 shadow-inner w-64" />
                    </div>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-left">
                        <thead className="bg-[#1e293b] text-slate-400 text-xs uppercase tracking-wider">
                            <tr>
                                <th className="p-4 rounded-tl-lg">Attestation ID</th>
                                <th className="p-4">Doc Type</th>
                                <th className="p-4">Issuer</th>
                                <th className="p-4">Holder Name</th>
                                <th className="p-4 rounded-tr-lg">Timestamp</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-700/50">
                            {physicals.map(a => {
                                let holderName = 'N/A';
                                if (a.metadata) {
                                    try {
                                        const m = typeof a.metadata === 'string' ? JSON.parse(a.metadata) : a.metadata;
                                        holderName = m.holderName || holderName;
                                    } catch(e) {}
                                }
                                return (
                                <tr key={a.id} className="hover:bg-slate-800/30 transition">
                                    <td className="p-4 font-mono text-xs text-sky-200/70">{a.id.substring(0, 16)}...</td>
                                    <td className="p-4 font-medium text-slate-200">{a.docType || 'Document'}</td>
                                    <td className="p-4 text-slate-400 text-sm max-w-[150px] truncate" title={a.issuer}>{a.issuer}</td>
                                    <td className="p-4 text-emerald-300/80 text-sm">{holderName}</td>
                                    <td className="p-4 text-slate-500 text-xs">{new Date(a.timestamp).toLocaleString()}</td>
                                </tr>
                                );
                            })}
                            {physicals.length === 0 && (
                                <tr><td colSpan="6" className="p-8 text-center text-slate-500 italic">No physical certificates found.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
            )}



            {/* QR Modal Overlay */}
            {qrModal && (
                <div className="fixed inset-0 bg-slate-900/80 backdrop-blur-md z-50 flex items-center justify-center p-4" onClick={(e) => e.target === e.currentTarget && setQrModal(null)}>
                    <div className="bg-[#0f172a] p-8 rounded-3xl max-w-sm w-full text-center flex flex-col items-center border border-slate-700 shadow-2xl relative">
                        <button onClick={() => setQrModal(null)} className="absolute top-4 right-4 text-slate-500 hover:text-white transition"><svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg></button>
                        <h2 className="text-2xl font-bold mb-2 bg-clip-text text-transparent bg-gradient-to-r from-emerald-400 to-cyan-400">Verifiable QR</h2>
                        <p className="text-xs text-slate-400 mb-6 font-mono break-all px-4">{qrModal}</p>
                        
                        <div className="bg-white p-3 rounded-2xl shadow-xl shadow-cyan-500/10 mb-8 border-4 border-slate-800">
                            <canvas id="qr-canvas"></canvas>
                        </div>
                        
                        <button onClick={() => {
                            const link = document.createElement('a');
                            link.download = `Certificate_${qrModal.substring(0,8)}.png`;
                            link.href = document.getElementById('qr-canvas').toDataURL();
                            link.click();
                        }} className="w-full py-3 bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-400 hover:to-cyan-400 text-white font-semibold rounded-xl shadow-lg shadow-emerald-500/20 active:scale-95 transition-all flex justify-center gap-2 items-center">
                            <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"></path></svg>
                            Download Identity Tag
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
